/*
 * Copyright (C) 2022 Worldline
 * All Rights Reserved
 * This product is protected by copyright. Any copying or distribution
 * is forbidden without the agreement of Worldline.
 */
import { OverlayModule } from '@angular/cdk/overlay';
import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from "@angular/material/chips";
import {MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from '@angular/material/tooltip';
import {BrowserModule, REMOVE_STYLES_ON_COMPONENT_DESTROY} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { NgbDatepickerI18n, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DlDateTimeDateModule, DlDateTimePickerModule } from 'angular-bootstrap-datetimepicker';
import { AngularDualListBoxModule } from 'angular-dual-listbox';
import { CanDeactivateGuard } from 'app/core/services/deactivate-route-guard.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { environment } from 'environments/environment';
import { NgChartsModule } from 'ng2-charts';
import { LoggerModule } from 'ngx-logger';
import { NotFoundComponent } from '../not-found/not-found.component';
import { SecurityModule } from '../security/security.module';
import { AppComponent } from './app.component';
import { ROUTES } from './app.routes';
import { BaseComponent } from './base/base.component';
import { BarChartComponent } from './charts/bar-chart/bar-chart.component';
import { DoughnutChartComponent } from './charts/doughnut-chart/doughnut-chart.component';
import { LineChartComponent } from './charts/line-chart/line-chart.component';
import { CoreModule } from './core/core.module';
import { NgbdSortableHeader } from './core/directives/sortable.directive';
import { AcceptanceAccountsService } from './core/services/acceptanceAccounts.service';
import { ConfigurationService } from './core/services/configuration.service';
import { ContractService } from './core/services/contract.service';
import { CustomDatepickerI18n } from './core/services/custom-datepicker-i18n.service';
import { NotifMessageService } from './core/services/notifmessage.service';
import { PaginationService } from './core/services/pagination.service';
import { PaymentTransactionsService } from './core/services/payment-transactions.service';
import { PosService } from './core/services/pos.service';
import { SupplierService } from './core/services/supplier.service';
import { TransactionService } from './core/services/transaction.service';
import { AcceptanceAccountCrudComponent } from './crud/acceptance-account-crud/acceptance-account-crud.component';
import { FuellingProviderCrudComponent } from './crud/fuellingprovider-crud/fuellingprovider-crud.component';
import { MerchantCrudComponent } from './crud/merchant-crud/merchant-crud.component';
import { PosCrudComponent } from './crud/pos-crud/pos-crud.component';
import { SupplierCrudComponent } from './crud/supplier-crud/supplier-crud.component';
import { FooterComponent } from './footer/footer.component';
import { ContactComponent } from './header/contact/contact.component';
import { FaqComponent } from './header/faq/faq.component';
import { HeaderComponent } from './header/header.component';
import { HelpComponent } from './header/help/help.component';
import { HomeComponent } from './home/home.component';
import { ActiveUsersReportsComponent } from './report/merchant/active-users-reports/active-users-reports.component';
import { CardsReportsComponent } from './report/merchant/cards-reports/cards-reports.component';
import { PaymentTransactionReportsComponent } from './report/merchant/payment-transaction-reports/payment-transaction-reports.component';
import { TransactionReportsComponent } from './report/merchant/transaction-reports/transaction-reports.component';
import { WalletReportsComponent } from './report/merchant/wallet-reports/wallet-reports.component';
import { WalletsEvolutionReportsComponent } from './report/merchant/wallets-evolution-reports/wallets-evolution-reports.component';
import { PosEvolutionReportsComponent } from './report/supplier/pos-evolution-reports/pos-evolution-reports.component';
import { SupplierTransactionReportsComponent } from './report/supplier/supplier-transaction-reports/supplier-transaction-reports.component';
import { TransactionEvolutionComponent } from './report/supplier/transaction-evolution/transaction-evolution.component';
import { SideNavItemsComponent } from './side-nav-items/side-nav-items.component';
import { AcceptanceAccountsPageComponent } from './table-pages/acceptance-accounts-page/acceptance-accounts-page.component';
import { AlertsPageComponent } from './table-pages/alerts-page/alerts-page.component';
import { ErrorsPageComponent } from './table-pages/errors-page/errors-page.component';
import { EventPageComponent } from './table-pages/event-page/event-page.component';
import { FuellingProviderPageComponent } from './table-pages/fuellingProvider-page/fuellingProvider-page.component';
import { MerchantPageComponent } from './table-pages/merchant-page/merchant-page.component';
import { PaymentTransactionPageComponent } from './table-pages/payment-transaction-page/payment-transaction-page.component';
import { PosPageComponent } from './table-pages/pos-page/pos-page.component';
import { SupplierPageComponent } from './table-pages/supplier-page/supplier-page.component';
import { TableComponent } from './table-pages/table-template/table-template.component';
import { TransactionsPageComponent } from './table-pages/transactions-page/transactions-page.component';
import { CreateUserComponent } from './user-pages/create-user/create-user.component';
import { EditUserComponent } from './user-pages/edit-user/edit-user.component';
import { SearchUserComponent } from './user-pages/search-user/search-user.component';
import { UsersPageComponent } from './user-pages/users-page/users-page.component';
import { AccessDeniedComponent } from './utilcomponent/access-denied/access-denied.component';
import { CallbacksComponent } from './utilcomponent/callbacks/callbacks.component';
import { ContactInfoComponent } from './utilcomponent/contact-info/contact-info.component';
import { EditContactInfoComponent } from './utilcomponent/edit-contact-info/edit-contact-info.component';
import { LocalizedDatePipe } from './utilcomponent/localized-date-pipe/localized-date.pipe';
import { MappingTablesComponent } from './utilcomponent/mapping-tables/mapping-tables.component';
import { ModalComponent, ModalContactExiste, ModalContentAlert, ModalContentDelete, ModalContentError, ModalContentEvent, ModalContentPaymentTransactionDetails, ModalContentProfile, ModalContentSaveNeeded, ModalContentTokenExpired, ModalContentTransactionDetails, ModalContentUser } from './utilcomponent/modals/modal/modal.component';
import { TransactionDetailsComponent } from './utilcomponent/modals/transaction-details/transaction-details.component';
import { SuppliersListComponent } from './utilcomponent/suppliers-list/suppliers-list.component';
import { TaxManagementComponent } from './utilcomponent/tax-management/tax-management.component';
import { MerchantSciComponent } from './utilcomponent/sci/merchant-sci.component';
import {CUSTOM_DATE_FORMATS} from "./utilcomponent/datepicker/date-formats";

registerLocaleData(localeFr);
registerLocaleData(localeEn);




export function initializeApp(appConfig: ConfigurationService) {
  return () => appConfig.load();
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}


@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        HomeComponent,
        NotFoundComponent,
        HelpComponent,
        FaqComponent,
        ContactComponent,
        MerchantPageComponent,
        SupplierPageComponent,
        PosPageComponent,
        ErrorsPageComponent,
        BaseComponent,
        EditContactInfoComponent,
        ModalComponent,
        ModalContentDelete,
        ModalContentSaveNeeded,
        ModalContentTokenExpired,
        ModalContentTransactionDetails,
        ModalContentPaymentTransactionDetails,
        ModalContentProfile,
        ModalContentUser,
        ModalContentEvent,
        ModalContentError,
        ModalContentAlert,
        NgbdSortableHeader,
        ModalContactExiste,
        TransactionsPageComponent,
        UsersPageComponent,
        CreateUserComponent,
        CreateUserComponent,
        EditUserComponent,
        SearchUserComponent,
        EventPageComponent,
        WalletReportsComponent,
        TransactionReportsComponent,
        PaymentTransactionReportsComponent,
        ActiveUsersReportsComponent,
        CardsReportsComponent,
        BarChartComponent,
        WalletsEvolutionReportsComponent,
        LineChartComponent,
        PosEvolutionReportsComponent,
        SupplierTransactionReportsComponent,
        TransactionEvolutionComponent,
        DoughnutChartComponent,
        FuellingProviderPageComponent,
        PaymentTransactionPageComponent,
        AcceptanceAccountsPageComponent,
        TransactionDetailsComponent,
        MerchantCrudComponent,
        SupplierCrudComponent,
        ContactInfoComponent,
        PosCrudComponent,
        FuellingProviderCrudComponent,
        MappingTablesComponent,
        AcceptanceAccountCrudComponent,
        TaxManagementComponent,
        AccessDeniedComponent,
        TableComponent,
        LocalizedDatePipe,
        SideNavItemsComponent,
        FooterComponent,
        AlertsPageComponent,
        CallbacksComponent,
        SuppliersListComponent,
        MerchantSciComponent
    ],
    imports: [
        MatTabsModule,
        MatCheckboxModule,
        MatSelectModule,
        MatDividerModule,
        MatRadioModule,
        MatCheckboxModule,
        MatSelectModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatCardModule,
        MatChipsModule,
        MatExpansionModule,
        MatToolbarModule,
        MatSidenavModule,
        MatIconModule,
        MatPaginatorModule,
        MatSortModule,
        MatTableModule,
        MatExpansionModule,
        MatIconModule,
        MatMenuModule,
        MatToolbarModule,
        MatSidenavModule,
        MatButtonModule,
        MatNativeDateModule,
        MatInputModule,
        MatDatepickerModule,
        MatMenuModule,
        CoreModule,
        NgbModule,
        MatMomentDateModule,
        NgChartsModule,
        BrowserAnimationsModule,
        BrowserModule,
        FormsModule, ReactiveFormsModule,
        RouterModule.forRoot(ROUTES, { enableTracing: false, useHash: true }),
        DlDateTimeDateModule,
        MatSnackBarModule,
        DlDateTimePickerModule,
        NgSelectModule,
        HttpClientModule,
        OverlayModule,
        // LoggerModule.forRoot({/*serverLoggingUrl: '/api/logs', */level: NgxLoggerLevel.DEBUG, serverLogLevel: NgxLoggerLevel.OFF }),
        LoggerModule.forRoot({
            serverLoggingUrl: environment.log.apiUrl, level: environment.log.clientLevelLog,
            serverLogLevel: environment.log.serverLevelLog
        }),
        LoggerModule.forChild(),
        SecurityModule,
        AngularDualListBoxModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        })
    ],
    providers: [
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'fr-FR'
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: CUSTOM_DATE_FORMATS
        },
        {
            provide: APP_BASE_HREF,
            useValue: '/'
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [ConfigurationService],
            multi: true
        },
        NotifMessageService,
        {
            provide: NgbDatepickerI18n,
            useClass: CustomDatepickerI18n
        },
        MerchantService,
        SupplierService,
        PosService,
        TransactionService,
        PaymentTransactionsService,
        PaginationService,
        ContractService,
        ConfigurationService,
        CanDeactivateGuard,
        AcceptanceAccountsService,
        {
          provide: REMOVE_STYLES_ON_COMPONENT_DESTROY,
          useValue: false
        }
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})

export class AppModule {
  constructor(
    configService: ConfigurationService
  ) { }
}
